import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { orderFormSpace } from 'feature/productOrder/namespaces/orderFormSpace'
import { v4 as uuid } from 'uuid'

import {
  AppealLoadingList,
  AppealStatus,
  TAppealFormImages,
  TEditAppealTimeSlot,
  TTimeSlotListItem,
} from '../type'

type TAppealFormState = {
  userSelectedSlots: TEditAppealTimeSlot[] | []
  availableSlotsList: TTimeSlotListItem[] | []
  productImages: orderFormSpace.Images[][] | []
  allImagesList: TAppealFormImages[] | []
  appealType: 'order' | 'lead' | ''
  editAppealLoading: {
    [key in AppealLoadingList]: boolean
  }
  isPullingStopped: boolean
  appealStatus: AppealStatus | null
}

const initialState: TAppealFormState = {
  userSelectedSlots: [],
  availableSlotsList: [],
  productImages: [],
  allImagesList: [],
  appealType: '',
  editAppealLoading: {
    appealInformation: false,
    appealManager: false,
    appealOffers: false,
    appealRecalcBlock: false,
  },
  isPullingStopped: false,
  appealStatus: null,
}

// слайс для методов, необходимых в формах редактирования заявки
const AppealFormSlice = createSlice({
  name: 'appealFormSlice',
  initialState,
  reducers: {
    // добавление выбранных таймслотов
    setSelectedTimeSlot: (
      state,
      action: PayloadAction<TEditAppealTimeSlot[]>,
    ) => {
      state.userSelectedSlots = action.payload
    },
    // добавление доступных таймслотов
    setAvaliableSlotList: (
      state,
      action: PayloadAction<TTimeSlotListItem[]>,
    ) => {
      state.availableSlotsList = action.payload
    },
    // обновление таймслота
    updateTimeSlot: (state, action: PayloadAction<TEditAppealTimeSlot>) => {
      const index = state.userSelectedSlots.findIndex(
        item => item.key === action.payload.key,
      )

      state.userSelectedSlots[index] = {
        ...state.userSelectedSlots[index],
        ...action.payload,
      }
    },
    // добавление таймслота
    addTimeSlot: state => {
      const newKey = uuid()

      state.userSelectedSlots = [
        ...state.userSelectedSlots,
        {
          id: state.availableSlotsList[0].id,
          date: '',
          key: newKey,
          isValidDate: false,
        },
      ]
    },
    // удаление таймслота
    removeTimeSlot: (state, action: PayloadAction<string>) => {
      state.userSelectedSlots = state.userSelectedSlots?.filter(
        slot => slot.key !== action.payload,
      )
    },
    // добавление всех файлов заявки
    addAllImagesList: (
      state,
      action: PayloadAction<TAppealFormImages[] | []>,
    ) => {
      state.allImagesList = [
        ...state.allImagesList.filter(
          item => item.paramId !== action.payload[0]?.paramId,
        ),
        ...action.payload,
      ]
    },
    // удаление картинки из общего списка
    removeImagesInAllList: (state, action: PayloadAction<number>) => {
      state.allImagesList = [
        ...state.allImagesList.filter(item => item.paramId !== action.payload),
      ]
    },
    // назначение типа заявки/заказа
    setAppealType: (state, action: PayloadAction<'order' | 'lead'>) => {
      state.appealType = action.payload
    },
    // добаление лоадера в нужный блок заявки
    setLoading: (
      state,
      action: PayloadAction<{
        type: AppealLoadingList
        value: boolean
      }>,
    ) => {
      const { type, value } = action.payload
      state.editAppealLoading = {
        ...state.editAppealLoading,
        [type]: value,
      }
    },
    // удаление лоадеров из всех блоков заявки
    setAllLoadingFalse: state => {
      state.editAppealLoading = {
        appealInformation: false,
        appealManager: false,
        appealOffers: false,
        appealRecalcBlock: false,
      }
    },
    // остановка пуллинга для запроса на получние данных о заявке
    setIsPullingStopped: (state, action: PayloadAction<boolean>) => {
      state.isPullingStopped = action.payload
    },
    // назначение статуса заявки
    setAppealStatus: (state, action: PayloadAction<AppealStatus>) => {
      state.appealStatus = action.payload
    },
  },
})

export const { actions, reducer } = AppealFormSlice

export const { setLoading, setIsPullingStopped, setAllLoadingFalse } = actions
